import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./verification_annonces.css";

// ⚙️ Mets à true si ton backend envoie l'email dans /annonce-validation (recommandé)
// Mets à false si tu veux que le front appelle /send-email en complément (fallback)
const EMAIL_SENT_SERVER_SIDE = true;

function VerificationAnnonces() {
  const navigate = useNavigate();

  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);

  // msg global en haut (succès/erreur)
  const [msg, setMsg] = useState("");

  // popup refus
  const [refuseId, setRefuseId] = useState(null);
  const [refuseComment, setRefuseComment] = useState("");

  // état d’action par ligne (pour éviter multi-clic)
  const [processing, setProcessing] = useState({ id: null, type: null }); // type: "validate" | "refuse" | null

  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  // sécurité minimale : si pas admin -> login/home
  useEffect(() => {
    if (!token || role !== "admin") navigate("/");
    // eslint-disable-next-line
  }, []);

  // Récupérer les annonces en attente
  const fetchAnnonces = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/annonces-en-attente`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) setAnnonces(Array.isArray(data) ? data : []);
      else setMsg(data.error || "Erreur lors du chargement.");
    } catch {
      setMsg("Erreur serveur.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAnnonces();
  }, [fetchAnnonces]);

  // Fallback d’envoi email si ton backend ne l’envoie pas encore
  const sendEmail = async (to, subject, text, html) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/send-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ to, subject, text, html }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      return true;
    } catch (err) {
      console.error("Erreur lors de l'envoi de l'email:", err);
      return false;
    }
  };

  const handleValidate = async (id) => {
    setMsg("");
    setProcessing({ id, type: "validate" });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/annonce-validation/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "valide" }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data?.error || "Erreur lors de l'opération.");
        return;
      }

      if (EMAIL_SENT_SERVER_SIDE) {
        setMsg(
          data?.emailSent
            ? "✅ Annonce validée ! ✉️ Email envoyé."
            : "✅ Annonce validée. ⚠️ Email non envoyé."
        );
      } else {
        // Fallback : envoi email côté front (déconseillé, mais dispo)
        const annonce = annonces.find((a) => a._id === id);
        const to = annonce?.userId?.email;
        if (!to) {
          setMsg(
            "✅ Annonce validée, mais ✖️ email introuvable pour l'envoi."
          );
        } else {
          const text = `Bonjour,

Votre annonce a été validée par l'administrateur.
Elle peut maintenant être publiée et visible par les utilisateurs.

Cordialement,
L'équipe PromeetService`;
          const ok = await sendEmail(
            to,
            "Votre annonce a été validée",
            text
          );
          setMsg(
            ok
              ? "✅ Annonce validée ! ✉️ Email envoyé."
              : "✅ Annonce validée, mais ✖️ l'email n'a pas pu être envoyé."
          );
        }
      }

      // Retirer la carte (pas besoin de refetch)
      setAnnonces((prev) => prev.filter((a) => a._id !== id));
    } catch {
      setMsg("Erreur serveur.");
    } finally {
      setProcessing({ id: null, type: null });
    }
  };

  const handleRefuse = async (id) => {
    setMsg("");
    setProcessing({ id, type: "refuse" });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/annonce-validation/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "refuse", commentaire: refuseComment }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data?.error || "Erreur lors de l'opération.");
        return;
      }

      if (EMAIL_SENT_SERVER_SIDE) {
        setMsg(
          data?.emailSent
            ? "❌ Annonce refusée. ✉️ Motif envoyé."
            : "❌ Annonce refusée. ⚠️ Email non envoyé."
        );
      } else {
        // Fallback : envoi email côté front (déconseillé, mais dispo)
        const annonce = annonces.find((a) => a._id === id);
        const to = annonce?.userId?.email;
        if (!to) {
          setMsg(
            "❌ Annonce refusée, mais ✖️ email introuvable pour l'envoi."
          );
        } else {
          const text = `Bonjour,

Votre annonce a été refusée par l'administrateur.
Motif : ${refuseComment}

Merci de la corriger puis de la soumettre à nouveau.

Cordialement,
L'équipe PromeetService`;
          const ok = await sendEmail(
            to,
            "Votre annonce a été refusée",
            text
          );
          setMsg(
            ok
              ? "❌ Annonce refusée. ✉️ Email envoyé."
              : "❌ Annonce refusée, mais ✖️ l'email n'a pas pu être envoyé."
          );
        }
      }

      // Clean popup + retirer la carte
      setAnnonces((prev) => prev.filter((a) => a._id !== id));
      setRefuseId(null);
      setRefuseComment("");
    } catch {
      setMsg("Erreur serveur.");
    } finally {
      setProcessing({ id: null, type: null });
    }
  };

  const isProcessing = (id) => processing.id === id;

  // ----- Helpers d'affichage -----

  const fmtDur = (h, m) => {
    const hh = Number(h) || 0;
    const mm = Number(m) || 0;
    return `${hh}h${String(mm).padStart(2, "0")}`;
    };

  const fmtPrice = (p) => {
    if (p === "" || p === null || p === undefined) return "—";
    const num = Number(p);
    if (Number.isNaN(num)) return String(p);
    return `${num} €`;
  };

  // Normalise d’éventuels anciens formats d’horaires (start/end au lieu de slots)
  const normalizeWorkingHours = (workingHours = []) => {
    return (Array.isArray(workingHours) ? workingHours : []).map((d) => {
      const isOpen = d?.isOpen !== false; // par défaut ouvert
      const slots = Array.isArray(d?.slots)
        ? d.slots
        : d?.start && d?.end
        ? [{ start: d.start, end: d.end }]
        : [];
      return {
        day: d?.day || "",
        isOpen,
        slots,
      };
    });
  };

  const apiBase = process.env.REACT_APP_API_URL;

  return (
    <div className="verif-admin-container">
      <h2>Vérification des annonces</h2>

      {msg && (
        <div
          className={`verif-admin-msg ${
            msg.startsWith("✅") || msg.startsWith("❌") ? "success" : "error"
          }`}
        >
          {msg}
        </div>
      )}

      {loading ? (
        <div className="verif-admin-loading">Chargement...</div>
      ) : annonces.length === 0 ? (
        <div className="verif-admin-empty">Aucune annonce en attente.</div>
      ) : (
        <div className="verif-annonces-list">
          {annonces.map((a) => {
            const imgUrl = a?.imagePath ? `${apiBase}${a.imagePath}` : null;

            return (
              <div className="verif-annonce-card" key={a._id}>
                {/* En-tête */}
                <div className="verif-card-head">
                  <div className="verif-card-title">
                    {a.nameCompany || "(Nom manquant)"}
                  </div>
                  <div className="verif-card-city">
                    {a.city || "—"} {a.postalCode || ""}
                  </div>
                </div>

                {/* Bloc contact + adresse + image */}
                <div className="verif-flex-row">
                  <div className="verif-col">
                    <div className="verif-card-info">
                      <b>Téléphone :</b> {a.phoneNumber || "—"}
                      <br />
                      <b>Email :</b> {a.userId?.email || "—"}
                    </div>

                    <div className="verif-card-info">
                      <b>Adresse :</b>{" "}
                      {a.adresCompany || a.address || "—"}
                      <br />
                      <b>Ville / CP :</b> {a.city || "—"} {a.postalCode || ""}
                      {Array.isArray(a.coordinates) &&
                        a.coordinates.length === 2 && (
                          <>
                            <br />
                            <b>Coordonnées :</b>{" "}
                            {a.coordinates[1]}, {a.coordinates[0]}
                          </>
                        )}
                    </div>
                  </div>

                  {imgUrl && (
                    <div className="verif-col verif-image-block">
                      <img
                        src={imgUrl}
                        alt="Logo / visuel"
                        style={{
                          maxWidth: 220,
                          maxHeight: 160,
                          borderRadius: 10,
                          border: "1px solid #eee",
                          objectFit: "contain",
                          background: "#fff",
                          padding: 6,
                        }}
                      />
                      <div style={{ marginTop: 6 }}>
                        <a href={imgUrl} target="_blank" rel="noreferrer">
                          Ouvrir l’image
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="verif-card-desc">
                  {a.description || "—"}
                </div>

                {/* Profils complets */}
                {Array.isArray(a.profils) && a.profils.length > 0 && (
                  <div className="verif-card-profils">
                    <b>Profils :</b>
                    <div className="verif-profils-grid">
                      {a.profils.map((p, i) => {
                        const wh = normalizeWorkingHours(p?.workingHours);

                        return (
                          <div className="verif-profil-card" key={i}>
                            <div className="verif-profil-head">
                              <div className="verif-profil-title">
                                {p?.name || "(Sans nom)"}
                              </div>
                              <div className="verif-profil-prof">
                                {p?.profession || "—"}
                              </div>
                            </div>

                            {/* Activités détaillées */}
                            <div className="verif-profil-section">
                              <div className="verif-profil-section-title">
                                Activités ({(p?.activities || []).length})
                              </div>
                              {(p?.activities || []).length === 0 ? (
                                <div className="verif-muted">—</div>
                              ) : (
                                <div className="verif-activities-table-wrap">
                                  <table className="verif-activities-table">
                                    <thead>
                                      <tr>
                                        <th>Nom</th>
                                        <th>Durée</th>
                                        <th>Prix</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(p.activities || []).map((act, j) => (
                                        <tr key={j}>
                                          <td>{act?.name || "—"}</td>
                                          <td>
                                            {fmtDur(
                                              act?.durationHours,
                                              act?.durationMinutes
                                            )}
                                          </td>
                                          <td>{fmtPrice(act?.price)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Horaires détaillés */}
                            <div className="verif-profil-section">
                              <div className="verif-profil-section-title">
                                Horaires
                              </div>
                              {wh.length === 0 ? (
                                <div className="verif-muted">—</div>
                              ) : (
                                <div className="verif-hours-grid">
                                  {wh.map((d, k) => (
                                    <div className="verif-hour-day" key={k}>
                                      <div className="verif-hour-dayname">
                                        {d.day || "—"}
                                      </div>
                                      <div className="verif-hour-status">
                                        {d.isOpen ? "Ouvert" : "Fermé"}
                                      </div>
                                      {d.isOpen && (
                                        <div className="verif-hour-slots">
                                          {(d.slots || []).length === 0
                                            ? "—"
                                            : (d.slots || [])
                                                .map(
                                                  (s) =>
                                                    `${s?.start || "??:??"}–${
                                                      s?.end || "??:??"
                                                    }`
                                                )
                                                .join("  •  ")}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="verif-card-btns">
                  <button
                    className="verif-btn approve"
                    onClick={() => handleValidate(a._id)}
                    disabled={isProcessing(a._id)}
                    title={
                      isProcessing(a._id)
                        ? "Action en cours..."
                        : "Valider"
                    }
                  >
                    {isProcessing(a._id) &&
                    processing.type === "validate"
                      ? "Validation..."
                      : "Valider"}
                  </button>

                  <button
                    className="verif-btn refuse"
                    onClick={() => {
                      setRefuseId(a._id);
                      setRefuseComment("");
                    }}
                    disabled={isProcessing(a._id)}
                    title={
                      isProcessing(a._id)
                        ? "Action en cours..."
                        : "Refuser"
                    }
                  >
                    Refuser
                  </button>
                </div>

                {/* Popup pour saisir le commentaire de refus */}
                {refuseId === a._id && (
                  <div className="verif-refuse-popup">
                    <textarea
                      value={refuseComment}
                      onChange={(e) => setRefuseComment(e.target.value)}
                      placeholder="Motif du refus (sera envoyé au professionnel)"
                      rows={3}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                    <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                      <button
                        className="verif-btn refuse"
                        onClick={() => handleRefuse(a._id)}
                        disabled={
                          !refuseComment.trim() || isProcessing(a._id)
                        }
                      >
                        {isProcessing(a._id) &&
                        processing.type === "refuse"
                          ? "Refus..."
                          : "Confirmer le refus"}
                      </button>
                      <button
                        className="verif-btn"
                        onClick={() => setRefuseId(null)}
                        disabled={isProcessing(a._id)}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default VerificationAnnonces;
